/* Versa Posh: small, shared motion and interaction helpers.
   - [data-vp-reveal]: fades a section in once, the first time it is 20% in view.
   - [data-vp-fabrics]: fabric buttons crossfade the room photo.
   - Accordion <details> open and close with a height animation.
   - [data-vp-finder]: filters the BoxDrop showroom list.
   - ?dealer=<slug> on any page is saved to the cart as a "dealer" attribute.
   - window.vpTrack(name, data): custom analytics events (see docs/rebuild/analytics.md).
   Values: docs/rebuild/AUDIT.md §5, rows 5 and 7. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  // Reveal ---------------------------------------------------------------
  function reveal(scope) {
    var els = Array.prototype.slice.call((scope || document).querySelectorAll('[data-vp-reveal]:not(.is-revealed)'));
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-revealed');
        io.unobserve(e.target);
      });
    }, { threshold: 0.2 });
    els.forEach(function (el) {
      // Anything already on screen at load shows immediately, no animation.
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-revealed');
      else io.observe(el);
    });
  }

  // Fabrics --------------------------------------------------------------
  function fabrics(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-vp-fabrics]'), function (section) {
      if (section.__vpFabrics) return;
      section.__vpFabrics = true;
      var buttons = Array.prototype.slice.call(section.querySelectorAll('[data-vp-fabric]'));
      var rooms = Array.prototype.slice.call(section.querySelectorAll('[data-vp-room]'));
      buttons.forEach(function (b) {
        b.addEventListener('click', function () {
          var i = b.getAttribute('data-vp-fabric');
          buttons.forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
          rooms.forEach(function (r) { r.classList.toggle('is-active', r.getAttribute('data-vp-room') === i); });
        });
      });
    });
  }

  // Accordions -----------------------------------------------------------
  // <details> can't animate its own height. Open first, then grow the content
  // from 0; close by shrinking the content, then clear [open].
  function animateDetails(details, opening) {
    var content = details.querySelector('.details__content, .accordion__content, .c-accordion__panel');
    if (!content || reduce || !content.animate) {
      details.open = opening;
      return;
    }
    if (details.__vpAnim) {
      var running = details.__vpAnim;
      details.__vpAnim = null;
      running.cancel();
    }
    if (opening) details.open = true;
    var full = content.scrollHeight;
    var frames = opening
      ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }]
      : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }];
    content.style.overflow = 'hidden';
    var anim = content.animate(frames, { duration: 260, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
    details.__vpAnim = anim;
    var done = false;
    function finish() {
      if (done || details.__vpAnim !== anim) return;
      done = true;
      details.__vpAnim = null;
      content.style.overflow = '';
      if (!opening) details.open = false;
    }
    anim.onfinish = finish;
    // Animations pause in background tabs; never leave a panel half-closed.
    window.setTimeout(finish, 340);
  }

  document.addEventListener('click', function (e) {
    var summary = e.target.closest('.section-accordion details > summary');
    if (!summary) return;
    var details = summary.parentElement;
    e.preventDefault();
    var opening = !details.open;
    // Exclusive accordions close their siblings (see unname() below).
    var name = details.getAttribute('data-vp-name');
    if (opening && name) {
      Array.prototype.forEach.call(document.querySelectorAll('details[data-vp-name="' + name + '"][open]'), function (other) {
        if (other !== details) animateDetails(other, false);
      });
    }
    animateDetails(details, opening);
  });

  // The browser closes sibling details[name] instantly, which would cut the
  // closing animation short. Manage exclusivity here instead.
  function unname(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('.section-accordion details[name]'), function (d) {
      d.setAttribute('data-vp-name', d.getAttribute('name'));
      d.removeAttribute('name');
    });
  }

  // Analytics -----------------------------------------------------------
  // Shopify customer events; a custom pixel forwards them to GA4 and Meta.
  window.vpTrack = function (name, data) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {});
    } catch (e) { /* never block the page for analytics */ }
  };

  // Dealer attribution -----------------------------------------------------
  function dealer() {
    var slug;
    try { slug = new URLSearchParams(window.location.search).get('dealer'); } catch (e) { return; }
    if (!slug) return;
    slug = slug.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 64);
    if (!slug) return;
    try {
      window.sessionStorage.setItem('vp-dealer', slug);
      if (window.sessionStorage.getItem('vp-dealer-sent') === slug) return;
    } catch (e) { /* storage blocked: still record it on the cart */ }
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { dealer: slug } })
    }).then(function (r) {
      if (!r.ok) return;
      try { window.sessionStorage.setItem('vp-dealer-sent', slug); } catch (e) { /* ignore */ }
      window.vpTrack('vp_dealer_link', { dealer: slug });
    }).catch(function () { /* attribution is best effort */ });
  }

  // Showroom finder ------------------------------------------------------
  function finder(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-vp-finder]'), function (section) {
      if (section.__vpFinder) return;
      section.__vpFinder = true;
      var form = section.querySelector('[data-vp-finder-form]');
      var input = section.querySelector('[data-vp-finder-input]');
      var status = section.querySelector('[data-vp-finder-status]');
      var items = Array.prototype.slice.call(section.querySelectorAll('[data-vp-dealer]'));
      var timer;

      function run(report) {
        var q = input.value.trim().toLowerCase();
        var shown = 0;
        var kind = !q ? 'none' : /^\d{3,5}$/.test(q) ? 'zip' : /^[a-z]{2}$/.test(q) ? 'state' : 'text';
        items.forEach(function (li) {
          var match = true;
          if (kind === 'zip') match = li.getAttribute('data-zip').slice(0, 3) === q.slice(0, 3);
          else if (kind === 'state') match = li.getAttribute('data-state') === q || li.getAttribute('data-search').indexOf(q) > -1;
          else if (kind === 'text') match = li.getAttribute('data-search').indexOf(q) > -1;
          li.hidden = !match;
          if (match) shown += 1;
        });
        if (!q) {
          status.textContent = items.length + ' showrooms listed.';
        } else if (!shown) {
          items.forEach(function (li) { li.hidden = false; });
          status.textContent = 'No showroom in our list matches \u201c' + input.value.trim() + '\u201d. Here is every showroom we list.';
        } else {
          status.textContent = shown + (shown === 1 ? ' showroom matches.' : ' showrooms match.');
        }
        if (report && q) window.vpTrack('vp_dealer_search', { query_type: kind, results: shown });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        run(true);
      });
      input.addEventListener('input', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { run(false); }, 150);
      });
      run(false);
    });
  }

  function init(scope) {
    unname(scope);
    reveal(scope);
    fabrics(scope);
    finder(scope);
  }
  root.classList.add('vp-js');
  dealer();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
